import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, FileText, DollarSign, CheckCircle2, TrendingUp, Clock, Zap } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Zoho Creator', icon: Database },
  { name: 'Zoho Analytics', icon: FileText },
  { name: 'Yelo OMS', icon: Cloud },
  { name: 'Tookan', icon: Cloud },
  { name: 'PDF Automation', icon: FileText }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '100%',
    label: 'Payment Automation',
    description: 'For 800-1000 delivery partners across multiple Caribbean markets'
  },
  {
    icon: Clock,
    value: '30 min',
    label: 'Processing Time',
    description: 'Reduced from 3 days to 30 minutes per payment cycle'
  },
  {
    icon: Zap,
    value: 'Zero',
    label: 'Excel Dependency',
    description: 'Eliminated completely by replacing manual formulas'
  },
  {
    icon: CheckCircle2,
    value: '99.8%',
    label: 'Payment Accuracy',
    description: 'Through automated validation rules and error detection'
  }
];

const highlights = [
  {
    category: 'Automation Excellence',
    items: [
      'Achieved 100% automation of payment processing for 800-1000 delivery partners across multiple Caribbean markets',
      'Reduced processing time from 3 days to 30 minutes per payment cycle through intelligent workflow automation',
      'Eliminated Excel dependency completely by replacing manual formulas with automated calculation engines'
    ]
  },
  {
    category: 'Operational Efficiency',
    items: [
      'Automated PDF generation and distribution of payment statements for all delivery partners',
      'Streamlined weekly payment cycles from manual nightmare to streamlined workflow requiring just a few clicks',
      'Enhanced payment accuracy to 99.8% through automated validation rules and error detection protocols'
    ]
  }
];

const challenges = [
  {
    title: 'Manual OMS Data Export and Processing',
    icon: Database,
    problem: 'Accounts team manually exported data from Yelo and Tookan OMS systems weekly, requiring individual handling of order data, delivery information, and commission calculations for hundreds of delivery partners, creating significant time bottlenecks.',
    solution: 'Our automation specialists implemented seamless Zoho ecosystem connectivity with automated OMS data integration from both Yelo and Tookan platforms. Real-time order and commission data synchronization eliminates manual export requirements and ensures immediate data availability for payment processing.',
    impact: 'Real-time OMS data integration implemented'
  },
  {
    title: 'Complex Excel-Based Commission Calculations',
    icon: FileText,
    problem: 'Payment calculations relied on intricate Excel formulas that were error-prone, difficult to audit, and required manual updates for each delivery partner\'s unique commission structure, rider bonuses, and penalty deductions.',
    solution: 'We developed a sophisticated Payment Logic Engine using Zoho Creator with custom data normalization processes that automatically handle structured payment calculations. The system manages segregated driver and merchant data processing with dedicated order processing table architecture for accurate commission computation.',
    impact: 'Automated commission calculation engine deployed'
  },
  {
    title: 'Time-Intensive Manual Payment Processing',
    icon: Clock,
    problem: 'Processing 800-1000 delivery partners weekly required individual manual handling for each payment, creating operational bottlenecks that consumed entire team resources for days during each payment cycle.',
    solution: 'Using Zoho Creator\'s workflow automation capabilities, we built an integrated payment processing system that automatically calculates commissions, applies bonuses and deductions, and generates payment instructions with minimal human intervention, reducing processing time to minutes.',
    impact: '3 days to 30 minutes processing time reduction'
  },
  {
    title: 'Lack of Automated Reporting',
    icon: Cloud,
    problem: 'No automated reporting meant hours of repetitive administrative work each payment cycle, with manual creation of payment statements and delivery partner documentation creating additional operational overhead.',
    solution: 'We implemented comprehensive Zoho Analytics dashboards providing real-time payment tracking, commission reconciliation, and performance metrics for delivery partners. Automated PDF generation creates branded payment statements with bulk distribution capabilities for seamless delivery partner communication.',
    impact: 'Automated reporting and PDF generation implemented'
  }
];

const features = [
  {
    title: 'Automated OMS Data Integration Platform',
    items: [
      'Real-time connectivity with Yelo and Tookan order management systems',
      'Seamless data synchronization eliminating manual export requirements',
      'Automated order and commission data processing with validation protocols'
    ]
  },
  {
    title: 'Custom Payment Logic Engine',
    items: [
      'Sophisticated commission calculation system built on Zoho Creator platform',
      'Automated handling of rider bonuses, penalties, and complex payment structures',
      'Structured data normalization with segregated driver and merchant processing'
    ]
  },
  {
    title: 'Comprehensive Analytics and Reporting System',
    items: [
      'Real-time Zoho Analytics dashboards for payment tracking and reconciliation',
      'Performance metrics monitoring for delivery partners across all markets',
      'Commission tracking with detailed breakdown and audit trail capabilities'
    ]
  },
  {
    title: 'Automated Document Generation Platform',
    items: [
      'Bulk PDF generation for delivery partner payment statements',
      'Branded documentation with automated distribution capabilities',
      'Custom payment summaries with detailed commission breakdowns'
    ]
  }
];

const PaymentAutomation = () => {
  return (
    <>
      <SEOHead
        title="Payment Automation Case Study | Agility"
        description="Automated payment processing reducing errors and improving efficiency."
        url="https://www.agilitytech.ai/case-studies/payment-automation"
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80"
            alt="Payment Automation Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Food Delivery/Logistics</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Payment Processing Automation
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Transforming weekly payment cycles for 1000+ delivery partners through intelligent 
              automation and document processing in a leading Caribbean food delivery platform.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Food Delivery/Logistics</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">Caribbean</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Multi-Market Operations</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q2 2024 - Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                <tech.icon className="h-6 w-6 text-teal-400" />
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
            <div className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
              <DollarSign className="h-6 w-6 text-teal-400" />
              <span className="text-gray-300 font-medium">Custom Payment Logic</span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <DollarSign className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Manual Payment Processing Crisis</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A leading food delivery platform operating across Caribbean markets faced significant 
                    operational challenges with their accounts team manually exporting OMS data and relying 
                    on complex Excel formulas for commission calculations and rider payments. Processing 
                    weekly payments for 800-1000 delivery partners required individual manual handling, 
                    creating bottlenecks, calculation errors, and operational inefficiencies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Complete Payment Automation Revolution</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced labor-intensive, error-prone manual processing with an intelligent, 
                    automated system that transformed payment operations across the entire food delivery 
                    platform. By implementing comprehensive Zoho ecosystem automation with sophisticated 
                    payment logic, we delivered operational excellence that exceeded all performance 
                    expectations.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Core Features Delivered</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <ul className="space-y-3">
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-300">
                      <ArrowRight className="flex-shrink-0 h-5 w-5 text-teal-400 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <DollarSign className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Automate Your Payment Processing?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our automation specialists to discover how payment automation can transform 
              your business operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PaymentAutomation;